import React, { useMemo, useState } from 'react';

const SHOWCASE_ITEMS = [
  {
    title: 'Website LaunchCare',
    category: 'Website',
    stage: 'Client-ready concept',
    price: 'From £199 deposit',
    timeline: '3–10 days',
    summary: 'High-converting website or landing page for local businesses that need better trust, enquiries and online presence.',
    bestFor: 'Trades, takeaways, salons, clinics, shops, consultants',
    features: ['Premium landing page', 'Contact CTA', 'SEO-ready structure', 'Mobile-first layout', 'Preview before acceptance'],
    route: '/smart-quote'
  },
  {
    title: 'AI Voice Assistant',
    category: 'AI',
    stage: 'Interactive business assistant',
    price: 'From £199+ setup',
    timeline: '3–7 days',
    summary: 'A branded assistant that welcomes visitors, answers questions, guides customers and captures leads.',
    bestFor: 'Service businesses, restaurants, agencies, booking-based companies',
    features: ['Welcome script', 'FAQ handling', 'Lead capture', 'Service explanation', 'Callback direction'],
    route: '/smart-quote'
  },
  {
    title: 'QR Campaign System',
    category: 'QR',
    stage: 'Campaign-ready',
    price: 'From £99 launch pack',
    timeline: '1–5 days',
    summary: 'QR posters, flyers and digital campaigns that send customers to offers, menus, booking pages or lead forms.',
    bestFor: 'Restaurants, takeaways, salons, events, local shops',
    features: ['Premium QR design', 'Offer page route', 'Tracking-ready CTA', 'Print/social use', 'NexoraX campaign styling'],
    route: '/smart-quote'
  },
  {
    title: 'Smart Quote Engine',
    category: 'Automation',
    stage: 'Live on NexoraX',
    price: 'Lead capture system',
    timeline: 'Already live',
    summary: 'A smart form that recommends the best digital package, gives a growth score and sends the brief into workflow.',
    bestFor: 'Agencies, service businesses, consultants, sales teams',
    features: ['Client brief capture', 'AI-style recommendation', 'Growth score', 'Reference number', 'Internal review flow'],
    route: '/smart-quote'
  },
  {
    title: 'Client Preview Portal',
    category: 'Client Portal',
    stage: 'Live on NexoraX',
    price: 'Included in workflow',
    timeline: 'Already live',
    summary: 'A client status portal where customers can track their brief, preview request and next action using a reference.',
    bestFor: 'Clients needing project updates without chasing',
    features: ['Reference lookup', 'Status timeline', 'Preview request', 'Callback request', 'Tracking link'],
    route: '/client-preview'
  },
  {
    title: 'Remote Workplace OS',
    category: 'Operations',
    stage: 'Internal live system',
    price: 'Custom system build',
    timeline: '7–21 days',
    summary: 'A secure team workspace for tasks, lead research, creative work, submissions, approvals and activity tracking.',
    bestFor: 'Remote teams, agencies, admin teams, sales teams',
    features: ['Staff access codes', 'Task assignment', 'Work submission', 'Admin approval', 'Activity timeline'],
    route: '/remote-workplace'
  },
  {
    title: 'CRM / Booking Dashboard',
    category: 'Dashboard',
    stage: 'Custom build',
    price: 'From £299+',
    timeline: '7–14 days',
    summary: 'A custom dashboard for jobs, enquiries, bookings, payments, clients, leads or operations.',
    bestFor: 'Businesses managing customers, bookings or jobs manually',
    features: ['Lead pipeline', 'Booking status', 'Client records', 'Admin view', 'Workflow tracking'],
    route: '/smart-quote'
  },
  {
    title: 'Mobile App / App-Style System',
    category: 'App',
    stage: 'Custom build',
    price: 'From £299+',
    timeline: '7–21 days',
    summary: 'A mobile-friendly app-style platform for customers, staff, ordering, booking, training or workflow.',
    bestFor: 'Businesses needing a mobile experience without starting from zero',
    features: ['Mobile-first UI', 'Customer journey', 'Portal flow', 'Secure areas', 'Future app expansion'],
    route: '/smart-quote'
  },
  {
    title: 'Social / UGC Campaign Pack',
    category: 'Marketing',
    stage: 'Campaign-ready',
    price: 'From £99+',
    timeline: '1–5 days',
    summary: 'Short-form campaign ideas, captions, hooks, hashtags, Canva direction and launch assets.',
    bestFor: 'TikTok, Instagram, Facebook and local launch campaigns',
    features: ['Caption hooks', 'Hashtag set', 'Canva direction', 'Offer angle', 'CTA wording'],
    route: '/smart-quote'
  }
];

const CATEGORIES = ['All', 'Website', 'AI', 'QR', 'Automation', 'Client Portal', 'Operations', 'Dashboard', 'App', 'Marketing'];

export default function NexoraXShowcaseGallery() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selected, setSelected] = useState(SHOWCASE_ITEMS[0]);

  const filtered = useMemo(() => {
    if (activeCategory === 'All') return SHOWCASE_ITEMS;
    return SHOWCASE_ITEMS.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  return (
    <main className="min-h-screen overflow-hidden bg-[#02030a] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_12%_10%,rgba(0,240,255,0.18),transparent_35%),radial-gradient(circle_at_88%_18%,rgba(168,85,247,0.22),transparent_35%),linear-gradient(180deg,#02030a,#050014_55%,#02030a)]" />
      <div className="pointer-events-none fixed inset-0 opacity-30 [background-image:linear-gradient(rgba(0,240,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.05)_1px,transparent_1px)] [background-size:42px_42px]" />

      <section className="relative mx-auto max-w-7xl px-5 py-10 md:py-16">
        <div className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_60px_rgba(0,240,255,0.12)] backdrop-blur-xl md:p-10">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.45em] text-cyan-300">
                NexoraX Showcase / Demo Gallery
              </p>
              <h1 className="mt-4 max-w-4xl text-4xl font-black leading-tight md:text-6xl">
                See what NexoraX can build for your business.
              </h1>
              <p className="mt-5 max-w-3xl text-slate-300">
                Explore websites, AI assistants, QR systems, client portals, dashboards, mobile app flows and marketing packs before requesting your own Smart Quote.
              </p>
            </div>

            <div className="rounded-3xl border border-purple-500/30 bg-purple-950/20 p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">Launch Path</p>
              <p className="mt-2 text-2xl font-black text-cyan-300">View → Choose → Quote</p>
              <a
                href="/smart-quote"
                className="mt-4 inline-flex rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50"
              >
                Start Smart Quote
              </a>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`rounded-xl border px-4 py-3 font-mono text-[10px] uppercase tracking-widest transition-all ${
                  activeCategory === category
                    ? 'border-cyan-400 bg-cyan-950/40 text-cyan-100 shadow-[0_0_20px_rgba(0,240,255,0.18)]'
                    : 'border-zinc-800 bg-black/40 text-slate-400 hover:border-purple-500/40'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="space-y-4">
            {filtered.map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => setSelected(item)}
                className={`w-full rounded-3xl border p-5 text-left transition-all ${
                  selected.title === item.title
                    ? 'border-cyan-400 bg-cyan-950/25 shadow-[0_0_35px_rgba(0,240,255,0.12)]'
                    : 'border-zinc-800 bg-black/60 hover:border-purple-500/40'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">
                    {item.category} • {item.timeline}
                  </p>
                  <span className="rounded-full border border-cyan-500/30 px-3 py-1 font-mono text-[9px] uppercase tracking-widest text-cyan-200">
                    {item.stage}
                  </span>
                </div>

                <h3 className="mt-3 text-2xl font-black text-white">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {item.summary}
                </p>
              </button>
            ))}
          </section>

          <section className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(168,85,247,0.10)] backdrop-blur-xl">
            <div className="rounded-[1.5rem] border border-cyan-500/20 bg-gradient-to-br from-cyan-950/20 via-black to-purple-950/20 p-6">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                Selected showcase
              </p>
              <h2 className="mt-3 text-3xl font-black md:text-4xl">
                {selected.title}
              </h2>
              <p className="mt-3 text-slate-300">
                {selected.summary}
              </p>

              <div className="mt-6 grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Best for</p>
                  <p className="mt-2 text-sm text-white">{selected.bestFor}</p>
                </div>

                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Timeline</p>
                  <p className="mt-2 text-sm font-bold text-cyan-300">{selected.timeline}</p>
                </div>

                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Starting point</p>
                  <p className="mt-2 text-sm font-bold text-purple-300">{selected.price}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-3xl border border-cyan-500/20 bg-cyan-950/10 p-5">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                What this can include
              </p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {selected.features.map((feature) => (
                  <div key={feature} className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm font-semibold text-cyan-100">
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-3xl border border-purple-500/20 bg-purple-950/10 p-5">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                Demo journey
              </p>
              <div className="mt-4 grid gap-3 md:grid-cols-4">
                {['Discover', 'Choose', 'Brief', 'Preview'].map((step, index) => (
                  <div key={step} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      Step {index + 1}
                    </p>
                    <p className="mt-2 text-sm font-bold text-white">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={`${selected.route}?service=${encodeURIComponent(selected.title)}`}
                className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50"
              >
                Request Similar Preview
              </a>

              <a
                href="/smart-quote"
                className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
              >
                Start Smart Quote
              </a>

              <a
                href="/client-preview"
                className="rounded-xl border border-pink-500/40 bg-pink-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-pink-100 hover:bg-pink-900/40"
              >
                Track Existing Brief
              </a>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
